/**
 * Adapters wire IRIS → IGliderRuntime (domain).
 *
 * Centralisent la traduction des champs raw (SEADEV/SEANAV/mod_list) vers la
 * façade allégée consommée par les composants. Une seule source de vérité pour
 * le mapping : si le backend renomme une colonne ou un mod_api_id, seul ce
 * fichier change.
 */

import { IIrisGliderState } from './IIrisGliderState'
import { ImodState } from './ImodState'
import {
  GLIDER_MODULE_API_IDS,
  GliderModuleApiId,
  IGliderDevices,
  IGliderNavTemplate,
  IGliderRuntime,
  MODULE_API_ID_TO_WIRE_KEY,
  getDefaultGliderRuntime,
} from './IGliderRuntime'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Convertit le bit numeric (0/1) IRIS en boolean strict — vide / undefined → false. */
function toBool(v: number | undefined | null): boolean {
  return v === 1
}

/** Convertit mod_list IRIS en `{ [api_id]: enable }`. Clé = mod_api_id (cf RG-MOD-001). */
export function adaptModList(
  modList: Array<ImodState> | null | undefined,
): Partial<Record<GliderModuleApiId, boolean>> {
  if (!modList?.length) return {}
  const allowed = new Set<string>(GLIDER_MODULE_API_IDS)
  const result: Partial<Record<GliderModuleApiId, boolean>> = {}
  for (const m of modList) {
    // Filtre défensif : si IRIS expose un nouveau mod inconnu côté front, on ignore
    // plutôt que de polluer le record. À ajouter explicitement dans GLIDER_MODULE_API_IDS
    // quand on veut le consommer.
    if (allowed.has(m.mod_api_id)) {
      result[m.mod_api_id as GliderModuleApiId] = m.enable
    }
  }
  return result
}

/** Extrait les flags périphériques depuis le SEADEV de gli_nmeas. Null-safe. */
export function adaptDevices(
  seadev:
    | { data_flr?: number; data_radio?: number; data_pld?: number; data_gps?: number; data_iridium?: number }
    | null
    | undefined,
): IGliderDevices {
  return {
    flasher: toBool(seadev?.data_flr),
    radio: toBool(seadev?.data_radio),
    payload: toBool(seadev?.data_pld),
    gps: toBool(seadev?.data_gps),
    iridium: toBool(seadev?.data_iridium),
  }
}

/** Extrait le profil nav depuis le SEANAV de gli_nmeas. Null si trame jamais reçue. */
export function adaptNavTemplate(
  seanav:
    | {
        data_bd?: number
        data_bu?: number
        data_pd?: number
        data_pu?: number
        data_zb?: number
        data_zt?: number
        data_zs?: number
        data_al?: number
        data_rate?: number
      }
    | null
    | undefined,
): IGliderNavTemplate | null {
  if (!seanav) return null
  return {
    bd: seanav.data_bd ?? 0,
    bu: seanav.data_bu ?? 0,
    pd: seanav.data_pd ?? 0,
    pu: seanav.data_pu ?? 0,
    zb: seanav.data_zb ?? 0,
    zt: seanav.data_zt ?? 0,
    zs: seanav.data_zs ?? 0,
    alt: seanav.data_al ?? 0, // ⚠ wire = data_al (cf IGliderInfos.ts SEANAV)
    rate: seanav.data_rate ?? 0,
  }
}

/**
 * Adapter principal — IIrisGliderState (réponse REST) → IGliderRuntime.
 *
 * gliId est passé explicitement car IIrisGliderState n'embarque que `gli_key`
 * (string) ; l'identifiant numérique vient de l'appelant qui connaît la mission.
 */
export function adaptIrisGliderStateToRuntime(state: IIrisGliderState, gliId: number | null): IGliderRuntime {
  const current = state.current
  const nmeas = current?.gli_nmeas
  return {
    gliId,
    targetLat: current?.gli_target_lat ?? null,
    targetLon: current?.gli_target_lon ?? null,
    targetPilot: current?.gli_target_pilot ?? null,
    mode: current?.gli_mode ?? null,
    alarmCode: current?.gli_alr ?? null,
    devices: adaptDevices(nmeas?.SEADEV),
    navTemplate: adaptNavTemplate(nmeas?.SEANAV),
    modules: adaptModList(state.mod_list),
    lastUpdate: new Date(),
  }
}

// ---------------------------------------------------------------------------
// Helpers WS — pour patches granulaires depuis WobGliderNmea / WobGliderNav /
// WobGliderAlarm / WobGliderModConf. Les mutations consomment ces helpers via
// `Vue.set` pour préserver la réactivité Vuex/Vue 2.
// ---------------------------------------------------------------------------

/**
 * Construit un objet "flat keyed" à partir d'une ligne `dataSet` + `columns`
 * d'une trame `wobGliderNmea`. Strip le préfixe 3-lettres SQL (ex SDE_) et
 * lowercase. ⚠ Cas particulier SEADRP (pas de préfixe — non géré ici, n'a pas
 * de consommateur runtime aujourd'hui).
 *
 * Exemple :
 *  columns=[{pos:5,dbName:'SDE_DATA_FLR'}, ...], row=[..., 1, ...]
 *  → { data_flr: 1, ... }
 */
export function flattenNmeaRow(
  columns: Array<{ pos: number; dbName: string }>,
  row: Array<unknown>,
): Record<string, unknown> {
  const obj: Record<string, unknown> = {}
  for (const col of columns) {
    const cleaned = col.dbName.replace(/^[A-Z]{3}_/, '').toLowerCase()
    obj[cleaned] = row[col.pos]
  }
  return obj
}

/** Réutilise adaptDevices/adaptNavTemplate depuis un row WS aplati. */
export { adaptDevices as deriveDevicesFromFlatRow }
export { adaptNavTemplate as deriveNavTemplateFromFlatRow }

// ---------------------------------------------------------------------------
// Utilitaire pour migrations progressives — créer un runtime à partir des
// défauts si pas encore initialisé sur une mission.
// ---------------------------------------------------------------------------

export { getDefaultGliderRuntime }
